import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  FileText, 
  RefreshCw,
  AlertCircle,
  Loader2,
  Edit,
  Printer,
  User,
  Calendar,
  Pill
} from 'lucide-react';
import { 
  getPrescriptions, 
  getPatients
} from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Prescription } from '@/types/prescription';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrescriptionDataTable from '@/components/Prescription/PrescriptionDataTable';
import SimpleFilters, { type SimpleFilterState } from '@/components/Prescription/SimpleFilters';
import PatientSearchDialog from '@/components/Patient/PatientSearchDialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
}



export default function PrescriptionsPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Prescriptions | OneCheckUp"; }, []);

  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Patient search modal for new prescription
  const [showPatientSearch, setShowPatientSearch] = useState<boolean>(false);

  // Simple filter states
  const [simpleFilters, setSimpleFilters] = useState<SimpleFilterState>({
    searchQuery: '',
    dateFilter: 'all',
    dateRange: { from: null, to: null },
    selectedMedicines: [],
    selectedComplaints: [],
    selectedPatients: []
  });


  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [prescriptionToPrint, setPrescriptionToPrint] = useState<Prescription | null>(null);

  const { currentUser } = useAuth();

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Handle navigation state and URL parameters
  useEffect(() => {
    const handleNavigationContext = () => {
      // Handle URL search parameters
      const filterParam = searchParams.get('filter');
      const patientParam = searchParams.get('patient');
      const modeParam = searchParams.get('mode');
      
      // Handle navigation state
      const state = location.state;
      
      // Set filter based on URL parameter - use simple filters
      if (filterParam === 'today') {
        setSimpleFilters(prev => ({
          ...prev,
          dateFilter: 'today'
        }));
      }
      
      // Set filter based on navigation state
      if (state?.filter === 'patient' && state?.patientId) {
        // When coming from patient-specific view, focus on that patient
        const patientId = state.patientId;
        const patientName = state.patientName;
        if (patientName) {
          setSimpleFilters(prev => ({
            ...prev,
            searchQuery: patientName
          }));
        }
      }
      
      // Handle mode-specific behavior
      if (state?.mode === 'create' || modeParam === 'consultation') {
        // Could trigger create prescription modal/flow here
        // console.log('Create prescription mode activated');
      }
      
      // Handle focus search from dashboard
      if (state?.focusSearch) {
        // Focus the search input when component mounts
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }
    };

    handleNavigationContext();
  }, [searchParams, location.state]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!currentUser?.doctor_id || !currentUser?.clinic_id) {
        setPatients([]);
        setPrescriptions([]);
        return;
      }
      // Load prescriptions and patients in parallel
      const [prescriptionsData, patientsData] = await Promise.all([
        getPrescriptions(),
        getPatients({
          clinic_id: currentUser.clinic_id
        })
      ]);


      if (prescriptionsData) {
        setPrescriptions(prescriptionsData);
      }

      if (patientsData && Array.isArray(patientsData.patients)) {
        setPatients(patientsData.patients);
      }
      

    } catch (err) {
      console.error('Error loading data:', err);
      const errorMessage = err?.message || 'Failed to load prescriptions. Please try again.';
      setError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadData();
      
      toast({
        title: "Success",
        description: "Data refreshed successfully",
        variant: "default"
      });
    } catch (err) {
      console.error('Refresh failed:', err);
      toast({
        title: "Error", 
        description: "Failed to refresh data",
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Get patient details for a prescription
  const getPatientDetails = (patientId) => {
    return patients.find(patient => patient.id === patientId) || {
      name: 'Unknown Patient',
      age: 0,
      gender: 'Unknown',
      phone: 'N/A'
    };
  };

  // Parse prescription data safely - now handles complete API response
  const parsePrescriptionData = (prescription) => {
    const patient = getPatientDetails(prescription.patient_id);
    
    return {
      ...prescription,
      patientName: patient.name,
      patientAge: patient.age,
      patientGender: patient.gender,
      patientPhone: patient.phone,
      // Data is now properly structured from API, no parsing needed
      complaints: prescription.complaints || [],
      medicines: prescription.medicines || [],
      investigations: prescription.investigations || [],
      vitals: prescription.vitals || {},
      next_visit: prescription.next_visit || null,
      // Keep string fields as-is
      advice: prescription.advice || '',
      diagnosis: prescription.diagnosis || '',
      quickNotes: prescription.quick_notes || ''
    };
  };

  // Enhanced filtering with simple filters
  const simpleFilteredPrescriptions = (prescriptions || [])
    .map(parsePrescriptionData)
    .filter((prescription) => {
      // Search filter
      const matchesSearch = !simpleFilters.searchQuery || 
        prescription.patientName?.toLowerCase().includes(simpleFilters.searchQuery.toLowerCase()) ||
        prescription.patientPhone?.includes(simpleFilters.searchQuery) ||
        prescription.id?.toLowerCase().includes(simpleFilters.searchQuery.toLowerCase());

      // Date filter logic
      let matchesDate = true;
      const prescriptionDate = new Date(prescription.created_at || prescription.updated_at || '');
      
      if (simpleFilters.dateFilter !== 'all') {
        const now = new Date();
        const prescriptionDateOnly = new Date(prescriptionDate.getFullYear(), prescriptionDate.getMonth(), prescriptionDate.getDate());
        
        if (simpleFilters.dateFilter === 'today') {
          const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          matchesDate = prescriptionDateOnly.getTime() === todayOnly.getTime();
        } else if (simpleFilters.dateFilter === 'week') {
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const weekStartOnly = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate());
          matchesDate = prescriptionDateOnly >= weekStartOnly;
        } else if (simpleFilters.dateFilter === 'month') {
          const monthStartOnly = new Date(now.getFullYear(), now.getMonth(), 1);
          matchesDate = prescriptionDateOnly >= monthStartOnly;
        }
      }

      // Custom date range filter (overrides quick date filter)
      if (simpleFilters.dateRange?.from || simpleFilters.dateRange?.to) {
        const prescriptionDateOnly = new Date(prescriptionDate.getFullYear(), prescriptionDate.getMonth(), prescriptionDate.getDate());
        
        if (simpleFilters.dateRange.from && simpleFilters.dateRange.to) {
          const fromDate = new Date(simpleFilters.dateRange.from.getFullYear(), simpleFilters.dateRange.from.getMonth(), simpleFilters.dateRange.from.getDate());
          const toDate = new Date(simpleFilters.dateRange.to.getFullYear(), simpleFilters.dateRange.to.getMonth(), simpleFilters.dateRange.to.getDate());
          matchesDate = prescriptionDateOnly >= fromDate && prescriptionDateOnly <= toDate;
        } else if (simpleFilters.dateRange.from) {
          const fromDate = new Date(simpleFilters.dateRange.from.getFullYear(), simpleFilters.dateRange.from.getMonth(), simpleFilters.dateRange.from.getDate());
          matchesDate = prescriptionDateOnly >= fromDate;
        } else if (simpleFilters.dateRange.to) {
          const toDate = new Date(simpleFilters.dateRange.to.getFullYear(), simpleFilters.dateRange.to.getMonth(), simpleFilters.dateRange.to.getDate());
          matchesDate = prescriptionDateOnly <= toDate;
        }
      }

      // Medicine filter - medicines are now properly structured objects
      const matchesMedicines = simpleFilters.selectedMedicines.length === 0 ||
        prescription.medicines?.some(med => 
          simpleFilters.selectedMedicines.includes(med.product || '')
        );

      // Complaint filter - complaints are now properly structured objects
      const matchesComplaints = simpleFilters.selectedComplaints.length === 0 ||
        prescription.complaints?.some(complaint => 
          simpleFilters.selectedComplaints.includes(complaint.complaint || '')
        );

      // Patient filter
      const matchesPatients = simpleFilters.selectedPatients.length === 0 ||
        simpleFilters.selectedPatients.includes(prescription.patientName || '');

      return matchesSearch && 
             matchesDate && matchesMedicines && matchesComplaints && matchesPatients;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at).getTime();
      const dateB = new Date(b.created_at || b.updated_at).getTime();
      return dateB - dateA; // Always desc order for latest first
    });

  // Use simple filtered prescriptions as the main filtered data
  const filteredPrescriptions = simpleFilteredPrescriptions;

  // Update total items when filtered data changes
  useEffect(() => {
    setTotalItems(filteredPrescriptions.length);
    // Reset to first page if current page would be empty
    const maxPage = Math.ceil(filteredPrescriptions.length / itemsPerPage);
    if (currentPage > maxPage && maxPage > 0) {
      setCurrentPage(1);
    }
  }, [filteredPrescriptions.length, itemsPerPage, currentPage]);

  // Get paginated data
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrescriptions = filteredPrescriptions.slice(startIndex, endIndex);

  // Pagination calculations
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(startIndex + itemsPerPage, totalItems);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Reset pagination when simple filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [simpleFilters]);



  const handleViewPrescription = (prescription) => {
    // Transform prescription before setting for modal
    const transformedPrescription = parsePrescriptionData(prescription);
    setSelectedPrescription(transformedPrescription);
  };

  const handleEditPrescription = (prescription) => {
    // Navigate to edit prescription page using React Router
    navigate(`/prescriptions/edit/${prescription.id}`);
  };

  const handlePrintPrescription = (prescription: Prescription) => {
    setPrescriptionToPrint(prescription);
    setShowPrintModal(true);
  };



  // Simple filter handlers
  const handleFiltersChange = (filters: SimpleFilterState) => {
    setSimpleFilters(filters);
    setCurrentPage(1); // Reset pagination when filters change
  };



  const handleExport = (format: 'csv' | 'pdf' | 'excel') => {
    // Export functionality implementation
    toast({
      title: "Export Started",
      description: `Exporting ${filteredPrescriptions.length} prescriptions as ${format.toUpperCase()}...`,
      variant: "default"
    });
    
    // TODO: Implement actual export logic
    // console.log(`Exporting ${filteredPrescriptions.length} prescriptions as ${format}`, filteredPrescriptions);
  };







  if (loading) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading prescriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescription-form-container">
        <div className="medical-card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Failed to Load Prescriptions</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadData} className="medical-button-primary">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="prescription-form-container">
      {/* Enterprise Header */}
      <div className="border-b border-border pb-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
                      <div>
              <h1 className="text-2xl font-bold text-foreground">Prescription Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage and track patient prescriptions efficiently
            </p>
          </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-9"
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
              Refresh
            </Button>
            
            <Button 
              onClick={() => setShowPatientSearch(true)}
              size="sm"
              className="h-9"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Prescription
            </Button>
          </div>
        </div>
      </div>

      {/* Prescription Management Content */}
      <div className="space-y-6">

        {/* Simple Search & Filters */}
        <SimpleFilters
          prescriptions={prescriptions}
          patients={patients}
          onFiltersChange={handleFiltersChange}
          onExport={handleExport}
          totalResults={filteredPrescriptions.length}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />



        {/* Simple Data Table */}
        <PrescriptionDataTable
          prescriptions={paginatedPrescriptions}
          patients={patients}
          loading={loading}
          onView={handleViewPrescription}
          onEdit={handleEditPrescription}
          onPrint={handlePrintPrescription}

          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredPrescriptions.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          hideSearchAndFilters={true}
        />
      </div>

      {/* Enhanced Prescription Detail Modal */}
      {selectedPrescription && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Prescription Details</h2>
                    <p className="text-sm text-gray-600">
                      {new Date(selectedPrescription.date || selectedPrescription.created_at).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditPrescription(selectedPrescription)}
                    className="h-8"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintPrescription(selectedPrescription)}
                    className="h-8"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Print
                  </Button>
                  <button
                    onClick={() => setSelectedPrescription(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="p-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Left Column - Patient & Basic Info */}
                  <div className="lg:col-span-1 space-y-4">
                    {/* Patient Info Card */}
                    <Card className="border-blue-200 bg-blue-50/30">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{(selectedPrescription as any).patientName}</h3>
                            <p className="text-xs text-gray-600">Patient Information</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-600">Age:</span>
                            <span className="text-xs font-medium">{(selectedPrescription as any).patientAge} years</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-600">Gender:</span>
                            <span className="text-xs font-medium">{(selectedPrescription as any).patientGender}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-600">Phone:</span>
                            <span className="text-xs font-medium">{(selectedPrescription as any).patientPhone}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Vitals Card (if available) */}
                    {selectedPrescription.vitals && Object.keys(selectedPrescription.vitals).length > 0 && (
                      <Card className="border-orange-200 bg-orange-50/30">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            Vitals
                          </h3>
                          <div className="space-y-2">
                            {Object.entries(selectedPrescription.vitals)
                              .filter(([key]) => !['prescription_id', 'tenant_id', 'last_modified'].includes(key))
                              .map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="text-xs text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                                <span className="text-xs font-medium">{value}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Next Visit Card (if available) */}
                    {selectedPrescription.next_visit && (
                      <Card className="border-purple-200 bg-purple-50/30">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-purple-600" />
                            Next Visit
                          </h3>
                          <div className="space-y-2">
                            {/* Combine date and time in one line */}
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-600">When:</span>
                              <span className="text-xs font-medium">
                                {selectedPrescription.next_visit.date}
                                {selectedPrescription.next_visit.time && ` at ${selectedPrescription.next_visit.time}`}
                              </span>
                            </div>
                            {selectedPrescription.next_visit.notes && (
                              <div className="flex justify-between">
                                <span className="text-xs text-gray-600">Notes:</span>
                                <span className="text-xs font-medium">{selectedPrescription.next_visit.notes}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Right Column - Medical Details */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Chief Complaints */}
                    {selectedPrescription.complaints.length > 0 && (
                      <Card className="border-blue-200">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <AlertCircle className="w-4 h-4 text-blue-600" />
                            Chief Complaints ({selectedPrescription.complaints.length})
                          </h3>
                          <div className="space-y-2">
                            {selectedPrescription.complaints.map((complaint: any, index: number) => {
                              // Combine non-important details into one line
                              const details = [
                                complaint.frequency && `Frequency: ${complaint.frequency}`,
                                complaint.severity && `Severity: ${complaint.severity}`,
                                complaint.duration && `Duration: ${complaint.duration}`
                              ].filter(Boolean).join(' • ');

                              return (
                                <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <div className="font-medium text-blue-900 mb-1 text-sm">{complaint.complaint}</div>
                                  {details && (
                                    <div className="text-xs text-gray-600">
                                      {details}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Medications */}
                    {selectedPrescription.medicines.length > 0 && (
                      <Card className="border-green-200">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <Pill className="w-4 h-4 text-green-600" />
                            Medications ({selectedPrescription.medicines.length})
                          </h3>
                          <div className="space-y-2">
                            {selectedPrescription.medicines.map((medicine: any, index: number) => {
                              // Combine non-important details into one line
                              const details = [
                                medicine.dosage && `Dosage: ${medicine.dosage}`,
                                (medicine.taken_when ?? (medicine as { when?: string }).when) && `When: ${medicine.taken_when ?? (medicine as { when?: string }).when}`,
                                medicine.duration && `Duration: ${medicine.duration}`,
                                medicine.quantity && `Qty: ${medicine.quantity}`
                              ].filter(Boolean).join(' • ');

                              return (
                                <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                                  <div className="font-semibold text-green-900 mb-1 text-sm">{medicine.brand_name || medicine.product || 'Unknown Medicine'}</div>
                                  {details && (
                                    <div className="text-xs text-gray-600">
                                      {details}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Investigations */}
                    {selectedPrescription.investigations.length > 0 && (
                      <Card className="border-amber-200">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Investigations ({selectedPrescription.investigations.length})
                          </h3>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex flex-wrap gap-1">
                              {selectedPrescription.investigations.map((investigation: any, index: number) => (
                                <span key={index} className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
                                  {typeof investigation === 'string' ? investigation : investigation.investigation}
                                </span>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Diagnosis */}
                    {selectedPrescription.diagnosis && (
                      <Card className="border-purple-200">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Diagnosis
                          </h3>
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <p className="text-purple-900 font-medium text-sm">{selectedPrescription.diagnosis}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Advice */}
                    {selectedPrescription.advice && (
                      <Card className="border-teal-200">
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Doctor's Advice
                          </h3>
                          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                            <p className="text-teal-900 leading-relaxed text-sm">{selectedPrescription.advice}</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Prescription Modal */}
      <PrintPrescriptionPreviewModal
        open={showPrintModal}
        prescription={prescriptionToPrint}
        onClose={() => {
          setShowPrintModal(false);
          setPrescriptionToPrint(null);
        }}
      />

             {/* Patient Search Modal */}
       <PatientSearchDialog
         isOpen={showPatientSearch}
         onClose={() => setShowPatientSearch(false)}
         onPatientSelect={(patient) => {
           // Navigate to patient detail page with prescription creation flag
           setShowPatientSearch(false);
           navigate(`/patients/${patient.id}?openPrescription=true`);
         }}
         title="Select Patient for New Prescription"
         description="Search and select a patient to create a new prescription"
         context="general"
       />
    </div>
  );
}